import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_LOADING } from '@fuse/services/loading/loading.interceptor';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, from, throwError } from 'rxjs';
import { catchError, last, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import {
    ICampaignFile,
    ICompletePartRequest,
    IFileUploadStatus,
    IInitiateUploadRequest,
    IInitiateUploadResponse,
    IInitiateUploadResponseRaw,
    IPresignedPart,
    IUploadProgress,
    UPLOAD_CONFIG
} from '../models/campaign-file.types';

@Injectable({
    providedIn: 'root'
})
export class CampaignFileUploadService {
    private readonly BASE_URL = '/api/v1/campaign/file';

    // Active uploads tracking
    private _uploads: BehaviorSubject<Map<string, IUploadProgress>> =
        new BehaviorSubject(new Map());

    // Upload events for UI notifications
    private _uploadComplete: Subject<{ fileId: string; filename: string }> = new Subject();
    private _uploadError: Subject<{ fileId: string; error: string }> = new Subject();

    public uploads$ = this._uploads.asObservable();
    public uploadComplete$ = this._uploadComplete.asObservable();
    public uploadError$ = this._uploadError.asObservable();

    constructor(private _httpClient: HttpClient) {}

    /**
     * Start uploading a file with multipart upload
     */
    uploadFile(campaignId: string, file: File): Observable<IUploadProgress> {
        // Validate file first
        const validationError = this.validateFile(file);
        if (validationError) {
            return throwError(() => new Error(validationError));
        }

        return this.initiateUpload(campaignId, file).pipe(
            switchMap(response => this.processUpload(response, file)),
            catchError(error => {
                this._uploadError.next({ fileId: '', error: error.message });
                return throwError(() => error);
            })
        );
    }

    /**
     * Cancel an ongoing upload
     */
    cancelUpload(fileId: string): Observable<void> {
        const uploads = this._uploads.value;
        const upload = uploads.get(fileId);

        if (upload) {
            upload.status = 'cancelled';
            this.updateUploadState(fileId, upload);
        }

        return this._httpClient.delete<void>(`${this.BASE_URL}/${fileId}/abort`);
    }

    /**
     * Resume a paused or failed upload
     */
    resumeUpload(fileId: string, file: File): Observable<IUploadProgress> {
        return this.getUploadStatus(fileId).pipe(
            switchMap(status => {
                if (status.status === 'completed') {
                    return throwError(() => new Error('Upload already completed'));
                }
                return this.regenerateUrls(fileId, file.size).pipe(
                    switchMap(response => this.resumeUploadFromStatus(response, file, status))
                );
            })
        );
    }

    /**
     * Get list of files for a campaign
     */
    getCampaignFiles(campaignId: string): Observable<ICampaignFile[]> {
        return this._httpClient.get<ICampaignFile[] | { data: ICampaignFile[] }>(`/api/v1/campaign/${campaignId}/files`).pipe(
            map(response => {
                // Handle both array response and wrapped { data: [...] } response
                if (Array.isArray(response)) {
                    return response;
                }
                return response.data || [];
            })
        );
    }

    /**
     * Delete a campaign file
     */
    deleteFile(fileId: string): Observable<void> {
        return this._httpClient.delete<void>(`${this.BASE_URL}/${fileId}`);
    }

    /**
     * Get download URL for a file
     */
    getDownloadUrl(fileId: string): Observable<{ url: string }> {
        return this._httpClient.get<{ url: string }>(`${this.BASE_URL}/${fileId}/download`);
    }

    /**
     * Get current upload status
     */
    getUploadStatus(fileId: string): Observable<IFileUploadStatus> {
        return this._httpClient.get<IFileUploadStatus>(`${this.BASE_URL}/${fileId}/status`);
    }

    /**
     * Validate file before upload
     */
    private validateFile(file: File): string | null {
        // Check file size
        if (file.size > UPLOAD_CONFIG.MAX_FILE_SIZE) {
            return `File size exceeds maximum allowed (${this.formatFileSize(UPLOAD_CONFIG.MAX_FILE_SIZE)})`;
        }

        // Check file type
        const extension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!UPLOAD_CONFIG.ALLOWED_EXTENSIONS.includes(extension)) {
            return `Invalid file type. Allowed: ${UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(', ')}`;
        }

        return null;
    }

    /**
     * Initiate multipart upload with backend
     */
    private initiateUpload(campaignId: string, file: File): Observable<IInitiateUploadResponse> {
        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        const request: IInitiateUploadRequest = {
            campaign_id: campaignId,
            original_name: file.name,
            extension: extension,
            mime_type: this.getContentType(file),
            file_size: file.size
        };

        return this._httpClient.post<any>(
            `${this.BASE_URL}/initiate`,
            request
        ).pipe(
            map(response => {
                // Handle wrapped response { data: ... }
                const raw: IInitiateUploadResponseRaw = response.data || response;
                if (!raw.presigned_urls || !Array.isArray(raw.presigned_urls)) {
                    throw new Error('Invalid response: missing presigned_urls array');
                }
                return this.mapRawResponseToNormalized(raw, file.size);
            })
        );
    }

    /**
     * Process the multipart upload
     */
    private processUpload(
        response: IInitiateUploadResponse,
        file: File
    ): Observable<IUploadProgress> {
        // Initialize upload progress
        const uploadProgress: IUploadProgress = {
            fileId: response.file_id,
            file: file,
            filename: file.name,
            totalSize: file.size,
            uploadedBytes: 0,
            progress: 0,
            status: 'uploading',
            startTime: new Date(),
            parts: response.parts.map(p => ({
                partNumber: p.part_number,
                status: 'pending',
                retries: 0
            }))
        };

        this.updateUploadState(response.file_id, uploadProgress);

        // Upload parts with concurrency control
        return this.uploadPartsWithConcurrency(response, file, uploadProgress).pipe(
            switchMap(completedParts => this.completeUpload(response.file_id, completedParts)),
            map(() => {
                uploadProgress.status = 'completed';
                uploadProgress.progress = 100;
                this.updateUploadState(response.file_id, uploadProgress);
                this._uploadComplete.next({
                    fileId: response.file_id,
                    filename: file.name
                });
                return uploadProgress;
            }),
            catchError(error => {
                uploadProgress.status = 'failed';
                uploadProgress.error = error.message;
                this.updateUploadState(response.file_id, uploadProgress);
                this._uploadError.next({ fileId: response.file_id, error: error.message });
                return throwError(() => error);
            })
        );
    }

    /**
     * Upload parts with concurrency control
     */
    private uploadPartsWithConcurrency(
        response: IInitiateUploadResponse,
        file: File,
        uploadProgress: IUploadProgress
    ): Observable<ICompletePartRequest[]> {
        const completedParts: ICompletePartRequest[] = [];

        return from(response.parts).pipe(
            mergeMap(part => {
                // Check if upload was cancelled
                const currentState = this._uploads.value.get(response.file_id);
                if (currentState?.status === 'cancelled') {
                    return throwError(() => new Error('Upload cancelled'));
                }

                return this.uploadPart(part, file, uploadProgress).pipe(
                    tap(result => completedParts.push(result))
                );
            }, UPLOAD_CONFIG.CONCURRENT_UPLOADS),
            // Wait for ALL parts to complete before emitting
            last(),
            // Return the sorted array of completed parts
            map(() => completedParts.sort((a, b) => a.part_number - b.part_number))
        );
    }

    /**
     * Upload a single part to presigned URL
     */
    private uploadPart(
        part: IPresignedPart,
        file: File,
        uploadProgress: IUploadProgress
    ): Observable<ICompletePartRequest> {
        const chunk = file.slice(part.start_byte, part.end_byte + 1);
        const partIndex = part.part_number - 1;

        // Update part status
        uploadProgress.parts[partIndex].status = 'uploading';
        this.updateUploadState(uploadProgress.fileId, uploadProgress);

        // Create XMLHttpRequest for direct upload to MinIO
        return new Observable<ICompletePartRequest>(observer => {
            const xhr = new XMLHttpRequest();

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    this.updatePartProgress(uploadProgress, partIndex, event.loaded);
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const etag = xhr.getResponseHeader('ETag')?.replace(/"/g, '') || '';

                    uploadProgress.parts[partIndex].status = 'completed';
                    uploadProgress.parts[partIndex].etag = etag;
                    this.updateUploadState(uploadProgress.fileId, uploadProgress);

                    // Notify backend about completed part
                    this.registerCompletedPart(uploadProgress.fileId, part.part_number, etag)
                        .subscribe({
                            next: () => {
                                observer.next({ part_number: part.part_number, etag });
                                observer.complete();
                            },
                            error: err => observer.error(err)
                        });
                } else {
                    this.handlePartError(part, file, uploadProgress, partIndex, observer,
                        new Error(`Upload failed with status ${xhr.status}`));
                }
            };

            xhr.onerror = () => {
                this.handlePartError(part, file, uploadProgress, partIndex, observer,
                    new Error('Network error during upload'));
            };

            xhr.open('PUT', part.presigned_url, true);
            xhr.setRequestHeader('Content-Type', 'application/octet-stream');
            xhr.send(chunk);
        });
    }

    /**
     * Handle part upload error with retry logic
     */
    private handlePartError(
        part: IPresignedPart,
        file: File,
        uploadProgress: IUploadProgress,
        partIndex: number,
        observer: any,
        error: Error
    ): void {
        uploadProgress.parts[partIndex].retries++;
        if (uploadProgress.parts[partIndex].retries < UPLOAD_CONFIG.MAX_RETRIES) {
            // Retry after delay
            setTimeout(() => {
                this.uploadPart(part, file, uploadProgress).subscribe(observer);
            }, UPLOAD_CONFIG.RETRY_DELAY * uploadProgress.parts[partIndex].retries);
        } else {
            uploadProgress.parts[partIndex].status = 'failed';
            this.updateUploadState(uploadProgress.fileId, uploadProgress);
            observer.error(new Error(`Part ${part.part_number} failed after ${UPLOAD_CONFIG.MAX_RETRIES} retries`));
        }
    }

    /**
     * Register completed part with backend
     */
    private registerCompletedPart(
        fileId: string,
        partNumber: number,
        etag: string
    ): Observable<void> {
        return this._httpClient.post<void>(
            `${this.BASE_URL}/${fileId}/part`,
            { part_number: partNumber, etag },
            { context: new HttpContext().set(SKIP_LOADING, true) }
        );
    }

    /**
     * Complete the multipart upload
     */
    private completeUpload(
        fileId: string,
        parts: ICompletePartRequest[]
    ): Observable<void> {
        return this._httpClient.post<void>(
            `${this.BASE_URL}/${fileId}/complete`,
            { parts },
            { context: new HttpContext().set(SKIP_LOADING, true) }
        );
    }

    /**
     * Regenerate presigned URLs for resuming upload
     */
    private regenerateUrls(fileId: string, fileSize: number): Observable<IInitiateUploadResponse> {
        return this._httpClient.post<any>(
            `${this.BASE_URL}/${fileId}/regenerate-urls`,
            {}
        ).pipe(
            map(response => this.mapRawResponseToNormalized(response.data || response, fileSize))
        );
    }

    /**
     * Map raw API response to normalized format
     */
    private mapRawResponseToNormalized(raw: IInitiateUploadResponseRaw, fileSize: number): IInitiateUploadResponse {
        const chunkSize = raw.chunk_size || UPLOAD_CONFIG.CHUNK_SIZE;
        const parts: IPresignedPart[] = raw.presigned_urls.map(p => ({
            part_number: p.part_number,
            presigned_url: p.url,
            start_byte: (p.part_number - 1) * chunkSize,
            end_byte: Math.min(p.part_number * chunkSize - 1, fileSize - 1)
        }));

        return {
            file_id: raw.file_id,
            upload_id: raw.upload_id,
            parts,
            expires_at: raw.expires_at,
            chunk_size: chunkSize,
            total_parts: raw.total_parts
        };
    }

    /**
     * Resume upload from existing status
     */
    private resumeUploadFromStatus(
        response: IInitiateUploadResponse,
        file: File,
        status: IFileUploadStatus
    ): Observable<IUploadProgress> {
        // Filter out already uploaded parts
        const remainingParts = response.parts.filter(
            p => !status.uploaded_parts.includes(p.part_number)
        );

        // Create modified response with only remaining parts
        const modifiedResponse: IInitiateUploadResponse = {
            ...response,
            parts: remainingParts
        };

        return this.processUpload(modifiedResponse, file);
    }

    /**
     * Update part progress and recalculate total progress
     */
    private updatePartProgress(
        uploadProgress: IUploadProgress,
        partIndex: number,
        bytesUploaded: number
    ): void {
        const completedPartBytes = uploadProgress.parts
            .slice(0, partIndex)
            .filter(p => p.status === 'completed')
            .length * UPLOAD_CONFIG.CHUNK_SIZE;

        uploadProgress.uploadedBytes = completedPartBytes + bytesUploaded;
        uploadProgress.progress = Math.round(
            (uploadProgress.uploadedBytes / uploadProgress.totalSize) * 100
        );

        // Calculate estimated time remaining
        if (uploadProgress.startTime) {
            const elapsed = Date.now() - uploadProgress.startTime.getTime();
            const bytesPerMs = uploadProgress.uploadedBytes / elapsed;
            const remainingBytes = uploadProgress.totalSize - uploadProgress.uploadedBytes;
            uploadProgress.estimatedTimeRemaining = Math.round(remainingBytes / bytesPerMs / 1000);
        }

        this.updateUploadState(uploadProgress.fileId, uploadProgress);
    }

    /**
     * Update the upload state in BehaviorSubject
     */
    private updateUploadState(fileId: string, progress: IUploadProgress): void {
        const uploads = new Map(this._uploads.value);
        uploads.set(fileId, { ...progress });
        this._uploads.next(uploads);
    }

    /**
     * Get content type for file
     */
    private getContentType(file: File): string {
        const extension = file.name.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'csv': return 'text/csv';
            case 'xls': return 'application/vnd.ms-excel';
            case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            default: return 'application/octet-stream';
        }
    }

    /**
     * Format file size for display
     */
    formatFileSize(bytes: number): string {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    }

    /**
     * Clear completed uploads from state
     */
    clearCompletedUploads(): void {
        const uploads = new Map(this._uploads.value);
        uploads.forEach((value, key) => {
            if (value.status === 'completed' || value.status === 'cancelled') {
                uploads.delete(key);
            }
        });
        this._uploads.next(uploads);
    }

    /**
     * Remove a specific upload from state
     */
    removeUploadFromState(fileId: string): void {
        const uploads = new Map(this._uploads.value);
        uploads.delete(fileId);
        this._uploads.next(uploads);
    }
}
